import React from 'react';
import { Table, Badge, Button, Avatar } from '@/components/ui';
import type { QaInspection, QaStatus, QaResult } from '../types/maintenance-qa.types';

export interface QaTableProps {
  data: QaInspection[];
  isLoading: boolean;
  onEdit: (item: QaInspection) => void;
  onRowClick: (item: QaInspection) => void;
}

const statusColors: Partial<Record<QaStatus, string>> = {
  draft: 'bg-slate-100 text-slate-500',
  pending_inspection: 'bg-amber-100 text-amber-600',
  inspecting: 'bg-blue-100 text-blue-600',
  passed: 'bg-emerald-100 text-emerald-600',
  failed: 'bg-red-100 text-red-600',
  rework_required: 'bg-indigo-100 text-indigo-600',
  rework_done: 'bg-indigo-200 text-indigo-800',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-slate-200 text-slate-700',
};

const resultColors: Partial<Record<QaResult, string>> = {
  pass: 'bg-emerald-100 text-emerald-600',
  fail: 'bg-red-100 text-red-600',
  pending: 'bg-slate-100 text-slate-400',
  na: 'bg-slate-50 text-slate-300',
};

export function QaTable({ data, isLoading, onEdit, onRowClick }: QaTableProps) {
  return (
    <Table
      columns={[
        { key: 'code', title: 'Mã đợt KT' },
        { key: 'target', title: 'Công việc & Dự án' },
        { key: 'inspector', title: 'Thanh tra' },
        { key: 'score', title: 'Điểm', align: 'center' },
        { key: 'result', title: 'Kết quả', align: 'center' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
        { key: 'actions', title: '', align: 'right' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'code':
            return <span className="font-mono text-[11px] font-bold text-slate-400  tracking-wider">{item.code}</span>;
          case 'target':
            return (
              <div className="flex flex-col">
                <span className="font-bold text-slate-900">{item.work_order?.name || 'Manual Inspection'}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-os-primary font-bold">{item.work_order?.order_number || '---'}</span>
                  <span className="text-slate-300">•</span>
                  <span className="text-[10px] text-slate-500 font-medium">{item.project?.name}</span>
                </div>
              </div>
            );
          case 'inspector':
            return (
              <div className="flex items-center gap-2">
                <Avatar src={item.inspector?.avatar_url} name={item.inspector?.full_name} size="xs" />
                <span className="text-xs font-bold text-slate-700">{item.inspector?.full_name}</span>
              </div>
            );
          case 'score':
            return (
              <div className="flex flex-col items-center">
                <span className="text-sm font-bold text-warning-600">{item.overall_score?.toFixed(1) || '---'}</span>
                <span className="text-[9px] text-slate-400  font-bold tracking-tighter">Hạng mục lỗi: {item.failed_items_count || 0}</span>
              </div>
            );
          case 'result':
            return (
              <Badge className={`${resultColors[item.final_result || 'pending']} border-transparent text-[10px] font-bold `}>
                {item.final_result || 'pending'}
              </Badge>
            );
          case 'status':
            return (
              <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold `}>
                {item.status_code.replace('_', ' ')}
              </Badge>
            );
          case 'actions':
            return (
              <div className="flex justify-end gap-1">
                <Button variant="ghost" size="sm" icon="ri-edit-line" onClick={(e) => { e.stopPropagation(); onEdit(item); }} />
              </div>
            );
          default:
            return null;
        }
      }}
    />
  );
}
